/* © NXAPP Limited */
import { useState, useRef, Suspense } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Papa from 'papaparse'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'
import { lazyWithReload } from '@/lib/lazyWithReload'
import { Upload, Plus, Users, Send, Clock, ChevronDown, ChevronRight, Lock, Unlock, MessageSquare, Ban, Check, PenLine, Phone } from 'lucide-react'
import Spinner from '@/components/ui/Spinner'
import EmptyState from '@/components/ui/EmptyState'
import { describeError } from '@/lib/describeError'
import { authedFetch } from '@/lib/authedFetch'

const SimpleEmailEditor = lazyWithReload(() => import('@/components/builder/SimpleEmailEditor'))

const FIELDS = [
  { key: 'email', label: 'Email', required: true },
  { key: 'first_name', label: 'First name', required: false },
  { key: 'last_name', label: 'Last name', required: false },
  { key: 'firm', label: 'Firm', required: false },
  { key: 'role', label: 'Role', required: false },
  { key: 'priority', label: 'Priority', required: false },
] as const
type FieldKey = typeof FIELDS[number]['key']

interface OContact { id: string; email: string; first_name: string | null; last_name: string | null; firm: string | null; role: string | null; status: string; contact_id: string | null }
interface Draft { id: string; contact_id: string; touch_no: number; subject: string; body: string; status: string; scheduled_for: string | null; sent_at: string | null; locked: boolean }

// New emails start with a greeting; the shared signature is appended at send.
const DEFAULT_BODY = 'Dear {{firstName}},<br /><br />'

/** Expand snippets + personalisation tags (no signature). */
function fill(text: string, c: OContact | undefined, presets: { tag: string; html: string }[]): string {
  let out = text || ''
  for (const p of presets) if (p.tag) out = out.replace(new RegExp(`\\{\\{\\s*${p.tag}\\s*\\}\\}`, 'gi'), p.html)
  return out
    .replace(/\{\{\s*firstName\s*\}\}/gi, c?.first_name || 'there')
    .replace(/\{\{\s*lastName\s*\}\}/gi, c?.last_name || '')
    .replace(/\{\{\s*(company|firm)\s*\}\}/gi, c?.firm || 'your firm')
}

/** Strip a single outer wrapper <div> (the editor's padded container). */
function unwrapDiv(html: string): string {
  const s = (html ?? '').trim()
  const m = s.match(/^<div\b[^>]*>([\s\S]*)<\/div>$/i)
  return m ? m[1].trim() : s
}

/** Render the email exactly as it will send: fill + trim + signature, one wrapper. */
function renderPreview(body: string, c: OContact | undefined, presets: { tag: string; html: string }[], signature: string): string {
  const trail = /(?:\s|&nbsp;|<br\s*\/?>|<p>\s*<\/p>|<div>\s*<\/div>|<p><br\s*\/?><\/p>)+$/i
  const lead = /^(?:\s|&nbsp;|<br\s*\/?>|<p>\s*<\/p>|<div>\s*<\/div>|<p><br\s*\/?><\/p>)+/i
  const out = unwrapDiv(fill(body, c, presets)).replace(trail, '')
  const sig = unwrapDiv(signature).replace(lead, '').replace(trail, '')
  return sig ? `${out}<br /><br />${sig}` : out
}

const ukStamp = (iso: string) => new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
}).format(new Date(iso))

export default function IndividualContent({ campaignId }: { campaignId: string }) {
  const { user } = useAuthContext()
  const qc = useQueryClient()
  const [error, setError] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)      // contact id
  const [activeTouch, setActiveTouch] = useState(1)
  const [showSig, setShowSig] = useState(false)
  const [showPresets, setShowPresets] = useState(false)
  const [showTm, setShowTm] = useState(false)
  const [newTm, setNewTm] = useState<string | null>(null)   // null = not creating a TM campaign
  const [newPreset, setNewPreset] = useState({ title: '', text: '' })
  const [onlyAttention, setOnlyAttention] = useState(false)
  const [tmMsg, setTmMsg] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  // Local edits, keyed by `${contactId}:${touch}`, so switching touch tabs never
  // loses what you typed. Persisted to the DB on Save / Schedule / Send.
  const [edits, setEdits] = useState<Record<string, { subject: string; body: string }>>({})
  const [schedAt, setSchedAt] = useState('')
  const [copied, setCopied] = useState('')
  const copyTag = (tag: string) => { navigator.clipboard?.writeText(tag).catch(() => {}); setCopied(tag); setTimeout(() => setCopied(c => c === tag ? '' : c), 1200) }
  const attFileRef = useRef<HTMLInputElement>(null)
  const attTargetRef = useRef(1)   // which touch the file picker is adding to
  const touchAttachments = (n: number) => config?.attachments?.[String(n)] ?? []

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data: config } = useQuery({
    queryKey: ['outreach-config', campaignId],
    queryFn: async () => {
      const { data } = await supabase.from('campaigns').select('outreach_config').eq('id', campaignId).single()
      return (data?.outreach_config ?? {}) as { signature?: string; touchCount?: number; businessHoursOnly?: boolean; offsets?: Record<string, { value: number; unit: string }>; presets?: { title: string; tag: string; text: string }[]; templates?: Record<string, { subject: string; body: string }>; attachments?: Record<string, { name: string; path: string; size: number; type: string }[]>; tm_campaign_id?: string; tm_auto_copy?: boolean }
    },
  })
  // Reusable snippets — insert their content into an email, or type {{tag}} and
  // it expands at send. Auto-links URLs and keeps line breaks.
  const linkify = (t: string) => (t ?? '')
    .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" style="color:#2563eb;">$1</a>')
    .replace(/\n/g, '<br />')
  const presets = (config?.presets ?? []).map(p => ({ title: p.title, tag: p.tag, html: linkify(p.text ?? '') }))
  const touchCount = Math.min(6, Math.max(1, config?.touchCount ?? 3))
  const offsetFor = (n: number) => config?.offsets?.[String(n)] ?? { value: 3, unit: 'days' }
  const saveOffset = (n: number, patch: { value?: number; unit?: string }) =>
    saveConfig.mutate({ offsets: { ...(config?.offsets ?? {}), [String(n)]: { ...offsetFor(n), ...patch } } })

  const saveConfig = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => {
      const { error } = await supabase.from('campaigns').update({ outreach_config: { ...(config ?? {}), ...patch } }).eq('id', campaignId)
      if (error) throw error
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach-config', campaignId] }),
  })

  const uploadAttachment = useMutation({
    mutationFn: async ({ touch, file }: { touch: number; file: File }) => {
      if (file.size > 25 * 1024 * 1024) throw new Error('File too large (max 25 MB).')
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const path = `${user!.id}/${campaignId}/t${touch}/${Date.now()}_${safe}`
      const { error } = await supabase.storage.from('outreach-files').upload(path, file, { upsert: false })
      if (error) throw error
      const list = config?.attachments?.[String(touch)] ?? []
      await supabase.from('campaigns').update({ outreach_config: { ...(config ?? {}), attachments: { ...(config?.attachments ?? {}), [String(touch)]: [...list, { name: file.name, path, size: file.size, type: file.type || 'application/octet-stream' }] } } }).eq('id', campaignId)
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach-config', campaignId] }),
  })
  const removeAttachment = (touch: number, path: string) => {
    supabase.storage.from('outreach-files').remove([path]).catch(() => {})
    const list = (config?.attachments?.[String(touch)] ?? []).filter(a => a.path !== path)
    saveConfig.mutate({ attachments: { ...(config?.attachments ?? {}), [String(touch)]: list } })
  }

  const { data: contacts, isLoading } = useQuery({
    queryKey: ['outreach-contacts', campaignId],
    queryFn: async () => {
      const { data } = await supabase.from('outreach_contacts')
        .select('id, email, first_name, last_name, firm, role, status, contact_id')
        .eq('campaign_id', campaignId).order('created_at', { ascending: false }).limit(500)
      return (data ?? []) as OContact[]
    },
  })

  const { data: drafts } = useQuery({
    queryKey: ['outreach-drafts', campaignId],
    queryFn: async () => {
      const { data } = await supabase.from('outreach_drafts')
        .select('id, contact_id, touch_no, subject, body, status, scheduled_for, sent_at, locked')
        .eq('campaign_id', campaignId).limit(3000)
      return (data ?? []) as Draft[]
    },
  })
  const draftFor = (contactId: string, touch: number) => (drafts ?? []).find(d => d.contact_id === contactId && d.touch_no === touch)

  // Dedicated TM campaigns this outreach can copy into. Empty target = this
  // campaign's own queue (self-linked). The link is OFF by default so the two
  // can stay independent (resale flexibility).
  const { data: tmCampaigns } = useQuery({
    queryKey: ['tm-campaign-options', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('campaigns').select('id, name').eq('user_id', user!.id).eq('kind', 'tm').order('name')
      return (data ?? []) as { id: string; name: string }[]
    },
  })
  // Where copies land: the chosen TM campaign, else this campaign itself.
  const tmTarget = config?.tm_campaign_id || campaignId

  // Create a dedicated TM campaign inline and immediately link this outreach to it.
  const createTmCampaign = useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await supabase.from('campaigns').insert({
        user_id: user!.id, name, description: '', colour: '#0d9488', icon: '📞',
        audience: 'director', kind: 'tm', sending_profile_id: null,
      }).select('id').single()
      if (error) throw error
      return data.id as string
    },
    onError: e => setError(describeError(e)),
    onSuccess: async (id) => {
      setNewTm(null)
      await qc.invalidateQueries({ queryKey: ['tm-campaign-options', user?.id] })
      saveConfig.mutate({ tm_campaign_id: id })
    },
  })

  // Who is already in the TM call queue — so the button reads "In TM" rather
  // than silently re-sending someone the caller is already working. Scoped to
  // wherever THIS campaign copies to (target or self).
  const { data: tmLeads } = useQuery({
    queryKey: ['tm-leads-ids', tmTarget],
    queryFn: async () => {
      const { data } = await supabase.from('tm_leads').select('outreach_contact_id').eq('campaign_id', tmTarget)
      return (data ?? []) as { outreach_contact_id: string }[]
    },
  })

  const refreshDrafts = () => qc.invalidateQueries({ queryKey: ['outreach-drafts', campaignId] })

  // ── Draft persistence ──────────────────────────────────────────────────────
  const saveDraft = useMutation({
    mutationFn: async ({ contactId, touch, patch }: { contactId: string; touch: number; patch: Partial<Draft> }) => {
      const existing = draftFor(contactId, touch)
      if (existing) {
        const { error } = await supabase.from('outreach_drafts').update(patch).eq('id', existing.id)
        if (error) throw error
        return existing.id
      }
      const { data, error } = await supabase.from('outreach_drafts').insert({
        user_id: user!.id, campaign_id: campaignId, contact_id: contactId, touch_no: touch,
        subject: '', body: '', status: 'draft', ...patch,
      }).select('id').single()
      if (error) throw error
      return data.id as string
    },
    onError: e => setError(describeError(e)),
    onSuccess: refreshDrafts,
  })

  const sendNow = useMutation({
    mutationFn: async (draftId: string) => {
      const res = await authedFetch('/api/outreach/send-one', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ draftId }),
      })
      const r = await res.json() as { ok: boolean; message: string }
      if (!r.ok) throw new Error(r.message)
      return r
    },
    onError: e => setError(describeError(e)),
    onSuccess: refreshDrafts,
  })

  // Current content for a (contact, touch): the local edit if present, else the
  // saved draft, else a fresh greeting.
  const keyFor = (contactId: string, touch: number) => `${contactId}:${touch}`
  const contentFor = (contactId: string, touch: number) => {
    const k = keyFor(contactId, touch)
    if (edits[k]) return edits[k]
    const d = draftFor(contactId, touch)
    if (d) return { subject: d.subject ?? '', body: d.body || DEFAULT_BODY }
    // No draft yet → start from the saved default template for this touch, if any.
    const tpl = config?.templates?.[String(touch)]
    return tpl ? { subject: tpl.subject ?? '', body: tpl.body || DEFAULT_BODY } : { subject: '', body: DEFAULT_BODY }
  }
  const setContent = (contactId: string, touch: number, patch: Partial<{ subject: string; body: string }>) =>
    setEdits(e => {
      const k = keyFor(contactId, touch)
      const d = draftFor(contactId, touch)
      const cur = e[k] ?? { subject: d?.subject ?? '', body: d?.body || DEFAULT_BODY }
      return { ...e, [k]: { ...cur, ...patch } }
    })

  // Persist the current content, then optionally schedule/send.
  const commitAndThen = async (contactId: string, touch: number, then?: 'schedule' | 'send') => {
    const cur = contentFor(contactId, touch)
    const d = draftFor(contactId, touch)
    const id = await saveDraft.mutateAsync({ contactId, touch, patch: { subject: cur.subject, body: cur.body, status: d?.status === 'scheduled' ? 'scheduled' : 'draft' } })
    if (then === 'schedule') {
      if (!schedAt) { setError('Pick a date and time to schedule.'); return }
      await supabase.from('outreach_drafts').update({ status: 'scheduled', scheduled_for: new Date(schedAt).toISOString() }).eq('id', id)
      refreshDrafts()
    } else if (then === 'send') {
      if (confirm('Send this email now? This goes out immediately and cannot be unsent.')) await sendNow.mutateAsync(id)
    }
  }

  // ── Contact management (import / add / pull / link) ─────────────────────────
  const [rawRows, setRawRows] = useState<Record<string, string>[] | null>(null)
  const [headers, setHeaders] = useState<string[]>([])
  const [map, setMap] = useState<Record<FieldKey, string>>({} as Record<FieldKey, string>)
  const onFile = (file: File) => {
    setError('')
    Papa.parse<Record<string, string>>(file, {
      header: true, skipEmptyLines: true,
      complete: res => {
        const cols = res.meta.fields ?? []
        setHeaders(cols); setRawRows(res.data)
        const guess = {} as Record<FieldKey, string>
        for (const f of FIELDS) {
          const hit = cols.find(c => c.toLowerCase().replace(/[^a-z]/g, '').includes(f.key.replace(/_/g, '')))
          if (hit) guess[f.key] = hit
        }
        setMap(guess)
      },
      error: err => setError(err.message),
    })
  }
  const linkToMembers = useMutation({
    mutationFn: async () => {
      const { data: unlinked } = await supabase.from('outreach_contacts')
        .select('id, email, first_name, last_name, firm').eq('campaign_id', campaignId).is('contact_id', null).limit(1000)
      if (!unlinked?.length) return { linked: 0 }
      const emails = [...new Set(unlinked.map(u => (u.email as string).toLowerCase()))]
      const map2 = new Map<string, string>()
      for (let i = 0; i < emails.length; i += 300) {
        const { data: ex } = await supabase.from('contacts').select('id, email').eq('user_id', user!.id).in('email', emails.slice(i, i + 300))
        for (const c of ex ?? []) map2.set((c.email as string).toLowerCase(), c.id as string)
      }
      const toCreate = unlinked.filter(u => !map2.has((u.email as string).toLowerCase()))
        .filter((u, i, a) => a.findIndex(x => (x.email as string).toLowerCase() === (u.email as string).toLowerCase()) === i)
      if (toCreate.length) {
        const { data: created } = await supabase.from('contacts').insert(toCreate.map(u => ({
          user_id: user!.id, email: u.email, first_name: u.first_name, last_name: u.last_name, company: u.firm, source: 'outreach', status: 'SUBSCRIBED',
        }))).select('id, email')
        for (const c of created ?? []) map2.set((c.email as string).toLowerCase(), c.id as string)
      }
      for (const u of unlinked) { const cid = map2.get((u.email as string).toLowerCase()); if (cid) await supabase.from('outreach_contacts').update({ contact_id: cid }).eq('id', u.id) }
      const ids = [...new Set([...map2.values()])]
      const assigned = new Set<string>()
      for (let i = 0; i < ids.length; i += 300) {
        const { data: ex } = await supabase.from('lead_campaign_assignments').select('contact_id').eq('campaign_id', campaignId).eq('target', 'contact').in('contact_id', ids.slice(i, i + 300))
        for (const a of ex ?? []) assigned.add(a.contact_id as string)
      }
      const news = ids.filter(id => !assigned.has(id)).map(id => ({ campaign_id: campaignId, contact_id: id, user_id: user!.id, target: 'contact', status: 'active', officer_id: null, notes: 'Individual Outreach' }))
      if (news.length) await supabase.from('lead_campaign_assignments').insert(news)
      return { linked: unlinked.length }
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['outreach-contacts', campaignId] }); qc.invalidateQueries({ queryKey: ['contacts'] }); qc.invalidateQueries({ queryKey: ['campaign_members'] }) },
  })
  const importRows = useMutation({
    mutationFn: async () => {
      if (!rawRows) return { inserted: 0 }
      if (!map.email) throw new Error('Map the Email column before importing.')
      const rows = rawRows.map(r => {
        const rec: Record<string, unknown> = { user_id: user!.id, campaign_id: campaignId }
        for (const f of FIELDS) rec[f.key] = map[f.key] ? (r[map[f.key]] ?? null) : null
        return rec
      }).filter(r => r.email && String(r.email).includes('@'))
      let inserted = 0
      for (let i = 0; i < rows.length; i += 500) {
        const { data, error } = await supabase.from('outreach_contacts').upsert(rows.slice(i, i + 500), { onConflict: 'campaign_id,email', ignoreDuplicates: true }).select('id')
        if (error) throw error
        inserted += data?.length ?? 0
      }
      return { inserted }
    },
    onError: e => setError(describeError(e)),
    onSuccess: async () => { setRawRows(null); setHeaders([]); setMap({} as Record<FieldKey, string>); await qc.invalidateQueries({ queryKey: ['outreach-contacts', campaignId] }); linkToMembers.mutate() },
  })
  const pullLeadMembers = useMutation({
    mutationFn: async () => {
      const { data: assigns } = await supabase.from('lead_campaign_assignments').select('contact_id, contacts(email, first_name, last_name, company)').eq('campaign_id', campaignId).eq('status', 'active').not('contact_id', 'is', null)
      const rows = (assigns ?? []).map(a => {
        const raw = (a as { contacts?: unknown }).contacts
        const c = (Array.isArray(raw) ? raw[0] : raw) as { email?: string; first_name?: string | null; last_name?: string | null; company?: string | null } | null
        if (!c?.email) return null
        return { user_id: user!.id, campaign_id: campaignId, contact_id: (a as { contact_id: string }).contact_id, email: c.email, first_name: c.first_name ?? null, last_name: c.last_name ?? null, firm: c.company ?? null }
      }).filter(Boolean) as Record<string, unknown>[]
      if (!rows.length) return { added: 0 }
      let added = 0
      for (let i = 0; i < rows.length; i += 500) { const { data } = await supabase.from('outreach_contacts').upsert(rows.slice(i, i + 500), { onConflict: 'campaign_id,email', ignoreDuplicates: true }).select('id'); added += data?.length ?? 0 }
      return { added }
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach-contacts', campaignId] }),
  })
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', firm: '', role: '' })
  const addOne = useMutation({
    mutationFn: async () => {
      if (!form.email.includes('@')) throw new Error('A valid email is required.')
      const { error } = await supabase.from('outreach_contacts').insert({ user_id: user!.id, campaign_id: campaignId, email: form.email.trim(), first_name: form.first_name || null, last_name: form.last_name || null, firm: form.firm || null, role: form.role || null })
      if (error) throw error
    },
    onError: e => setError(describeError(e)),
    onSuccess: async () => { setForm({ email: '', first_name: '', last_name: '', firm: '', role: '' }); setShowAdd(false); await qc.invalidateQueries({ queryKey: ['outreach-contacts', campaignId] }); linkToMembers.mutate() },
  })
  const setContactStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => { const { error } = await supabase.from('outreach_contacts').update({ status }).eq('id', id); if (error) throw error },
    onError: e => setError(describeError(e)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['outreach-contacts', campaignId] }),
  })

  const openContact = (c: OContact) => {
    const next = expanded === c.id ? null : c.id
    setExpanded(next); if (next) { setActiveTouch(1); setSchedAt('') }
  }
  const openTouch = (_contactId: string, touch: number) => { setActiveTouch(touch); setSchedAt('') }

  // Contacts that have no scheduled or sent touch anywhere.
  const needsAttention = (c: OContact) => !(drafts ?? []).some(d => d.contact_id === c.id && (d.status === 'scheduled' || d.status === 'sent'))
  const visibleContacts = (contacts ?? []).filter(c => !onlyAttention || needsAttention(c))
  const unlinkedCount = (contacts ?? []).filter(c => !c.contact_id).length

  // ── Bulk prepare Touch 1 ────────────────────────────────────────────────────
  // Individual Outreach only sends drafts you've written + scheduled, so an
  // imported list (e.g. from Auto Prospect) sends nothing until Touch 1 exists.
  // This creates a Touch-1 draft from the saved default template for every active
  // contact that hasn't got one, and schedules them STAGGERED (so it drips, not
  // blasts). Touch 2/3 then auto-chain as each Touch 1 sends. Business-hours hold
  // (if on) still applies at send time.
  const needsTouch1 = (contacts ?? []).filter(c => c.status === 'active' && !draftFor(c.id, 1))
  const prepareTouch1 = useMutation({
    mutationFn: async () => {
      const tpl = config?.templates?.['1']
      if (!tpl || !(tpl.body ?? '').trim()) {
        throw new Error('Write a Touch 1 default first: open any contact’s Touch 1, write the email (use {{firstName}}/{{company}}), then click “Save as default”. Then run this again.')
      }
      const targets = needsTouch1
      if (!targets.length) throw new Error('Every active contact already has a Touch 1.')
      const gapMs = 5 * 60_000                                  // ~1 every 5 min → ~12/hour
      const now = Date.now()
      const rows = targets.map((c, i) => ({
        user_id: user!.id, campaign_id: campaignId, contact_id: c.id, touch_no: 1,
        subject: tpl.subject ?? '', body: tpl.body ?? '', status: 'scheduled',
        scheduled_for: new Date(now + i * gapMs).toISOString(),
      }))
      for (let i = 0; i < rows.length; i += 100) {
        const { error } = await supabase.from('outreach_drafts').insert(rows.slice(i, i + 100))
        if (error) throw error
      }
      return rows.length
    },
    onError: e => setError(describeError(e)),
    onSuccess: n => { setError(''); setTmMsg(`Prepared & scheduled Touch 1 for ${n} contact${n === 1 ? '' : 's'} — dripping ~1 every 5 min${config?.businessHoursOnly ? ', held to Mon–Fri 09:00–17:00' : ''}. Touch 2/3 auto-follow as each sends.`); refreshDrafts() },
  })

  // ── TM hand-off ───────────────────────────────────────────────────────────
  // Only Touch 1 having actually SENT makes someone callable — that send is the
  // reason the call has a reference point ("Asif emailed you on Tuesday…").
  const touch1Sent = (c: OContact) => draftFor(c.id, 1)?.status === 'sent'
  const tmEligible = (contacts ?? []).filter(c => c.status === 'active' && touch1Sent(c))
  const inTM = new Set((tmLeads ?? []).map(l => l.outreach_contact_id))

  const sendToTM = useMutation({
    mutationFn: async (payload: { contactIds?: string[]; allTouch1Sent?: boolean }) => {
      const r = await authedFetch('/api/tm/handoff', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId, targetCampaignId: config?.tm_campaign_id || undefined, ...payload }),
      })
      const j = await r.json() as { ok: boolean; message: string }
      if (!r.ok) throw new Error(j.message || 'Hand-off failed')
      return j
    },
    onError: e => setError(describeError(e)),
    onSuccess: j => { setError(''); setTmMsg(j.message); qc.invalidateQueries({ queryKey: ['tm-leads-ids', tmTarget] }) },
  })

  const touchPill = (c: OContact, n: number) => {
    const d = draftFor(c.id, n)
    if (!d || d.status === 'draft') return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${d ? 'bg-gray-50 text-gray-500 border-gray-200' : 'bg-white text-gray-300 border-gray-200'}`}>{d ? `T${n} draft` : `T${n} —`}</span>
    if (d.status === 'scheduled') return <span className="text-[10px] px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200" title={d.scheduled_for ? ukStamp(d.scheduled_for) : ''}>T{n} ⏱ {d.scheduled_for ? ukStamp(d.scheduled_for) : ''}</span>
    if (d.status === 'sent') return <span className="text-[10px] px-1.5 py-0.5 rounded border bg-green-50 text-green-700 border-green-200" title={d.sent_at ? `Sent ${ukStamp(d.sent_at)}` : ''}>T{n} ✓ {d.sent_at ? ukStamp(d.sent_at) : 'sent'}</span>
    return <span className="text-[10px] px-1.5 py-0.5 rounded border bg-red-50 text-red-600 border-red-200">T{n} failed</span>
  }

  return (
    <div className="space-y-5">
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">{error}<button onClick={() => setError('')} className="ml-3 text-red-400 hover:text-red-600">✕</button></div>}
      {tmMsg && <div className="rounded-md bg-teal-50 border border-teal-200 px-4 py-2 text-sm text-teal-800">{tmMsg}<button onClick={() => setTmMsg('')} className="ml-3 text-teal-400 hover:text-teal-700">✕</button></div>}
      <input ref={attFileRef} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) uploadAttachment.mutate({ touch: attTargetRef.current, file: f }); e.target.value = '' }} />

      <div className="rounded-lg border border-teal-200 bg-teal-50/60 px-4 py-2.5 text-xs text-teal-800">
        <strong>Individual Outreach.</strong> Add a contact, then write a distinct email for each touch. Lock it if you like, then
        schedule it for a time or send now. The signature below is shared and appended to every email.
      </div>

      {/* Flow settings — set ONCE for the whole campaign (count + gaps) */}
      <section className="border border-gray-200 rounded-lg px-3 py-2.5 space-y-2">
        <p className="text-sm font-medium text-gray-700">Flow settings <span className="text-[11px] font-normal text-gray-400">— applies to every contact</span></p>
        <label className="text-xs text-gray-500 flex items-center gap-2">
          Number of touches
          <input type="number" min={1} max={6} defaultValue={touchCount} key={`tc-${touchCount}`}
            onBlur={e => Number(e.target.value) !== touchCount && saveConfig.mutate({ touchCount: Math.min(6, Math.max(1, Number(e.target.value))) })}
            className="w-16 rounded-md border border-gray-300 px-2 py-1" />
        </label>
        <label className="text-xs text-gray-600 flex items-center gap-2">
          <input type="checkbox" checked={!!config?.businessHoursOnly} onChange={e => saveConfig.mutate({ businessHoursOnly: e.target.checked })} />
          Only send during business hours (Mon–Fri 09:00–17:00 UK) — held outside those hours
        </label>
        {touchCount > 1 && (
          <div className="space-y-1">
            <p className="text-[11px] font-medium text-gray-500">Follow-up gaps (each auto-schedules after the previous touch sends)</p>
            {Array.from({ length: touchCount - 1 }, (_, i) => i + 2).map(n => {
              const off = offsetFor(n)
              return (
                <div key={n} className="flex items-center gap-1.5 text-xs text-gray-600">
                  <span className="w-16">Touch {n}:</span> +
                  <input type="number" min={0} defaultValue={off.value} key={`ov-${n}-${off.value}`}
                    onBlur={e => Number(e.target.value) !== off.value && saveOffset(n, { value: Number(e.target.value) })}
                    className="w-14 rounded-md border border-gray-300 px-1.5 py-0.5" />
                  <select defaultValue={off.unit} key={`ou-${n}-${off.unit}`} onChange={e => saveOffset(n, { unit: e.target.value })} className="rounded-md border border-gray-300 px-1 py-0.5">
                    <option value="minutes">minutes</option><option value="hours">hours</option><option value="days">days</option>
                  </select>
                  <span className="text-gray-400">after Touch {n - 1}</span>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* Copy Data to TM — the telemarketing link (collapsible, OFF by default) */}
      <section className="border border-gray-200 rounded-lg">
        <button onClick={() => setShowTm(v => !v)} className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700">
          <span className="flex items-center gap-1.5"><Phone className="w-4 h-4" /> Copy Data to TM
            <span className="text-[11px] font-normal text-gray-400">
              — {config?.tm_auto_copy ? 'auto after Touch 1' : 'manual'}
              {config?.tm_campaign_id ? ` → ${tmCampaigns?.find(t => t.id === config.tm_campaign_id)?.name ?? 'chosen TM campaign'}` : ' → no TM campaign selected'}
            </span>
          </span>
          {showTm ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {showTm && (
          <div className="px-3 pb-3 space-y-3">
            <p className="text-[11px] text-gray-400">
              Once Touch 1 has sent, a contact can be copied into a TM (telemarketing) call queue.
              This link is optional — leave it off and the two stay independent.
            </p>
            <div className="text-xs">
              <span className="text-gray-500 block mb-0.5">Send copies to</span>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={config?.tm_campaign_id ?? ''}
                  onChange={e => saveConfig.mutate({ tm_campaign_id: e.target.value || null })}
                  className="input py-1 text-sm w-full max-w-sm">
                  <option value="">— Select a TM campaign —</option>
                  {(tmCampaigns ?? []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {newTm === null ? (
                  <button onClick={() => setNewTm('')} className="btn-secondary text-xs flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> New TM campaign</button>
                ) : (
                  <span className="flex items-center gap-1">
                    <input autoFocus value={newTm} onChange={e => setNewTm(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && newTm.trim()) createTmCampaign.mutate(newTm.trim()); if (e.key === 'Escape') setNewTm(null) }}
                      placeholder="TM campaign name" className="input py-1 text-sm w-44" />
                    <button onClick={() => newTm.trim() && createTmCampaign.mutate(newTm.trim())} disabled={createTmCampaign.isPending || !newTm.trim()} className="btn-primary text-xs disabled:opacity-50">{createTmCampaign.isPending ? '…' : 'Create'}</button>
                    <button onClick={() => setNewTm(null)} className="btn-secondary text-xs">✕</button>
                  </span>
                )}
              </div>
              {!tmCampaigns?.length && newTm === null && (
                <span className="text-[11px] text-gray-400 mt-1 block">
                  No TM campaigns yet — create one to copy contacts into a call queue.
                </span>
              )}
            </div>
            <label className="text-xs text-gray-600 flex items-start gap-2">
              <input type="checkbox" className="mt-0.5" checked={!!config?.tm_auto_copy} onChange={e => saveConfig.mutate({ tm_auto_copy: e.target.checked })} />
              <span>Automatically copy each contact to TM the moment their Touch 1 sends. This only affects future sends — use the button below to copy contacts already sent.</span>
            </label>

            {/* Backlog copy — everyone whose Touch 1 already sent, in one click. */}
            <div className="pt-1 border-t border-gray-100">
              {(() => {
                const backlog = tmEligible.filter(c => !inTM.has(c.id)).length
                return (
                  <>
                    <button
                      onClick={() => {
                        if (!config?.tm_campaign_id) { setTmMsg('Choose a TM campaign above to copy into first.'); return }
                        if (!backlog) { setTmMsg('Everyone whose Touch 1 has sent is already in TM.'); return }
                        const dest = tmCampaigns?.find(t => t.id === config.tm_campaign_id)?.name ?? 'the TM campaign'
                        if (confirm(`Copy ${backlog} existing contact${backlog === 1 ? '' : 's'} whose Touch 1 has sent into ${dest}?`)) sendToTM.mutate({ allTouch1Sent: true })
                      }}
                      disabled={sendToTM.isPending}
                      className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50">
                      <Phone className="w-3.5 h-3.5" />
                      {sendToTM.isPending ? 'Copying…' : `Copy ${backlog} existing Touch-1-sent contact${backlog === 1 ? '' : 's'} now`}
                    </button>
                    <p className="text-[11px] text-gray-400 mt-1">
                      Copies the backlog — everyone whose Touch 1 has already sent and who isn’t in TM yet — into the selected campaign.
                    </p>
                  </>
                )
              })()}
            </div>
          </div>
        )}
      </section>

      {/* Shared signature (collapsible) */}
      <section className="border border-gray-200 rounded-lg">
        <button onClick={() => setShowSig(v => !v)} className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700">
          <span className="flex items-center gap-1.5"><PenLine className="w-4 h-4" /> Shared signature</span>
          {showSig ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {showSig && (
          <div className="px-3 pb-3">
            <Suspense fallback={<Spinner />}>
              <SimpleEmailEditor initialHtml={config?.signature ?? ''} onChange={html => saveConfig.mutate({ signature: html })} />
            </Suspense>
          </div>
        )}
      </section>

      {/* Reusable snippets / presets (collapsible) */}
      <section className="border border-gray-200 rounded-lg">
        <button onClick={() => setShowPresets(v => !v)} className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700">
          <span className="flex items-center gap-1.5"><PenLine className="w-4 h-4" /> Snippets ({presets.length})</span>
          {showPresets ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        {showPresets && (
          <div className="px-3 pb-3 space-y-2">
            <p className="text-[11px] text-gray-400">Reusable blocks of text (e.g. a standard opening paragraph). Click a snippet in an email’s tag row to insert it, or type its <code>{'{{tag}}'}</code> and it expands when sent.</p>
            {(config?.presets ?? []).map((p, i) => (
              <div key={p.tag} className="border border-gray-200 rounded-lg p-2 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-800">{p.title}</span>
                  <code className="text-[10px] text-teal-600">{`{{${p.tag}}}`}</code>
                  <button onClick={() => saveConfig.mutate({ presets: (config?.presets ?? []).filter((_, j) => j !== i) })} className="ml-auto text-gray-400 hover:text-red-600 text-[11px]">delete</button>
                </div>
                <textarea defaultValue={p.text} rows={3} key={`pt-${p.tag}`}
                  onBlur={e => e.target.value !== p.text && saveConfig.mutate({ presets: (config?.presets ?? []).map((x, j) => j === i ? { ...x, text: e.target.value } : x) })}
                  className="w-full rounded-md border border-gray-200 px-2 py-1 text-xs" />
              </div>
            ))}
            <div className="border border-dashed border-gray-300 rounded-lg p-2 space-y-1">
              <input placeholder="Snippet title (e.g. Opening paragraph)" value={newPreset.title} onChange={e => setNewPreset(s => ({ ...s, title: e.target.value }))} className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" />
              <textarea placeholder="The text…" value={newPreset.text} onChange={e => setNewPreset(s => ({ ...s, text: e.target.value }))} rows={3} className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs" />
              <button
                onClick={() => {
                  const title = newPreset.title.trim(); if (!title || !newPreset.text.trim()) { setError('Give the snippet a title and text.'); return }
                  const base = title.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'snippet'
                  const taken = new Set((config?.presets ?? []).map(p => p.tag))
                  let tag = base, n = 2; while (taken.has(tag)) tag = `${base}_${n++}`
                  saveConfig.mutate({ presets: [...(config?.presets ?? []), { title, tag, text: newPreset.text }] })
                  setNewPreset({ title: '', text: '' })
                }}
                className="btn-secondary text-xs">Add snippet</button>
            </div>
          </div>
        )}
      </section>

      {/* Contacts toolbar */}
      <section>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5"><Users className="w-4 h-4" /> Contacts ({visibleContacts.length}{onlyAttention ? ` of ${contacts?.length ?? 0}` : ''})</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[11px] text-gray-500 flex items-center gap-1"><input type="checkbox" checked={onlyAttention} onChange={e => setOnlyAttention(e.target.checked)} /> Needs attention</label>
            {needsTouch1.length > 0 && (
              <button
                onClick={() => { if (confirm(`Prepare & schedule Touch 1 for ${needsTouch1.length} contact${needsTouch1.length === 1 ? '' : 's'} from your Touch 1 default template?\n\nThey drip out ~1 every 5 minutes${config?.businessHoursOnly ? ' (held to business hours)' : ''}. You can still edit or cancel any before it sends.`)) prepareTouch1.mutate() }}
                disabled={prepareTouch1.isPending}
                className="text-[11px] text-white bg-teal-600 hover:bg-teal-700 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50">
                <Send className="w-3.5 h-3.5" />
                {prepareTouch1.isPending ? 'Preparing…' : `Prepare Touch 1 (${needsTouch1.length})`}
              </button>
            )}
            {tmEligible.length > 0 && (
              <button
                onClick={() => {
                  if (!config?.tm_campaign_id) { setShowTm(true); setTmMsg('Choose a TM campaign to copy into first — see “Copy Data to TM” above.'); return }
                  const n = tmEligible.filter(c => !inTM.has(c.id)).length
                  if (!n) { setTmMsg('Everyone with Touch 1 sent is already in TM.'); return }
                  const dest = tmCampaigns?.find(t => t.id === config.tm_campaign_id)?.name ?? 'the TM campaign'
                  if (confirm(`Copy ${n} contact${n === 1 ? '' : 's'} whose Touch 1 has sent to ${dest}?`)) sendToTM.mutate({ allTouch1Sent: true })
                }}
                disabled={sendToTM.isPending}
                className="text-[11px] text-teal-700 border border-teal-300 bg-teal-50 hover:bg-teal-100 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50">
                <Phone className="w-3.5 h-3.5" />
                {sendToTM.isPending ? 'Copying…' : `Copy ${tmEligible.filter(c => !inTM.has(c.id)).length} to TM`}
              </button>
            )}
            {unlinkedCount > 0 && <button onClick={() => linkToMembers.mutate()} disabled={linkToMembers.isPending} className="text-[11px] text-amber-700 border border-amber-300 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded disabled:opacity-50">Link {unlinkedCount} to members</button>}
            <button onClick={() => pullLeadMembers.mutate()} disabled={pullLeadMembers.isPending} className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50"><Users className="w-3.5 h-3.5" /> Pull in lead members</button>
            <button onClick={() => setShowAdd(v => !v)} className="btn-secondary text-xs flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add contact</button>
            <button onClick={() => fileRef.current?.click()} className="btn-secondary text-xs flex items-center gap-1"><Upload className="w-3.5 h-3.5" /> Import CSV</button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = '' }} />
          </div>
        </div>

        {showAdd && (
          <div className="border border-gray-200 rounded-lg p-3 mb-3 bg-gray-50">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <input placeholder="Email *" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="input py-1 text-sm" />
              <input placeholder="First name" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} className="input py-1 text-sm" />
              <input placeholder="Last name" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} className="input py-1 text-sm" />
              <input placeholder="Firm" value={form.firm} onChange={e => setForm(f => ({ ...f, firm: e.target.value }))} className="input py-1 text-sm" />
              <input placeholder="Role" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className="input py-1 text-sm" />
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => setShowAdd(false)} className="btn-secondary text-xs">Cancel</button>
              <button onClick={() => addOne.mutate()} disabled={addOne.isPending || !form.email.includes('@')} className="btn-primary text-xs disabled:opacity-50">Add contact</button>
            </div>
          </div>
        )}

        {rawRows && (
          <div className="border border-gray-200 rounded-lg p-3 mb-3 bg-gray-50 space-y-2">
            <p className="text-xs font-medium text-gray-700">Map columns ({rawRows.length} rows)</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {FIELDS.map(f => (
                <label key={f.key} className="text-xs"><span className="text-gray-500">{f.label}{f.required && <span className="text-red-500"> *</span>}</span>
                  <select value={map[f.key] ?? ''} onChange={e => setMap(m => ({ ...m, [f.key]: e.target.value }))} className="input py-1 mt-0.5 text-xs">
                    <option value="">— none —</option>{headers.map(h => <option key={h} value={h}>{h}</option>)}
                  </select></label>
              ))}
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setRawRows(null); setHeaders([]) }} className="btn-secondary text-xs">Cancel</button>
              <button onClick={() => importRows.mutate()} disabled={importRows.isPending || !map.email} className="btn-primary text-xs disabled:opacity-50">{importRows.isPending ? 'Importing…' : `Import ${rawRows.length}`}</button>
            </div>
          </div>
        )}

        {isLoading ? <div className="py-6 flex justify-center"><Spinner /></div>
          : !contacts?.length ? <EmptyState icon={Users} title="No contacts yet" description="Add a contact or import a CSV to begin." />
          : (
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
              {visibleContacts.map(c => (
                <div key={c.id}>
                  {/* Row */}
                  <div className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50">
                    <button onClick={() => openContact(c)} className="flex items-center gap-2 flex-1 min-w-0 text-left">
                      {expanded === c.id ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
                      <span className="min-w-0">
                        <span className="font-medium text-gray-900 text-sm">{c.first_name || c.email}</span>
                        <span className="block text-[11px] text-gray-400 truncate">{c.email}{c.firm ? ` · ${c.firm}` : ''}</span>
                      </span>
                    </button>
                    <div className="flex items-center gap-1 flex-wrap justify-end">
                      {Array.from({ length: touchCount }, (_, i) => <span key={i}>{touchPill(c, i + 1)}</span>)}
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0 ml-1">
                      {/* TM hand-off — only once Touch 1 has actually sent. */}
                      {c.status === 'active' && touch1Sent(c) && (
                        inTM.has(c.id)
                          ? <span title="Already in the TM call queue" className="p-1 text-teal-600"><Phone className="w-4 h-4" /></span>
                          : <button onClick={() => { if (!config?.tm_campaign_id) { setShowTm(true); setTmMsg('Choose a TM campaign to copy into first — see “Copy Data to TM” above.'); return } sendToTM.mutate({ contactIds: [c.id] }) }} disabled={sendToTM.isPending}
                              title="Copy to TM (telemarketing) call queue"
                              className="p-1 text-gray-400 hover:text-teal-700 hover:bg-teal-50 rounded disabled:opacity-50"><Phone className="w-4 h-4" /></button>
                      )}
                      {c.status === 'active'
                        ? <>
                            <button onClick={() => setContactStatus.mutate({ id: c.id, status: 'replied' })} title="Replied — pause" className="p-1 text-blue-600 hover:bg-blue-50 rounded"><MessageSquare className="w-4 h-4" /></button>
                            <button onClick={() => confirm(`Mark ${c.email} opted out?`) && setContactStatus.mutate({ id: c.id, status: 'opted_out' })} title="Opt out" className="p-1 text-gray-400 hover:text-red-600 rounded"><Ban className="w-4 h-4" /></button>
                          </>
                        : <button onClick={() => setContactStatus.mutate({ id: c.id, status: 'active' })} title="Resume" className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>}
                    </div>
                  </div>

                  {/* Expanded: touch tabs + editor */}
                  {expanded === c.id && (() => {
                    const d = draftFor(c.id, activeTouch)
                    const isSent = d?.status === 'sent'
                    const locked = d?.locked ?? false
                    const content = contentFor(c.id, activeTouch)
                    return (
                      <div className="px-3 pb-3 bg-gray-50/60">
                        <div className="flex flex-wrap gap-1.5 my-2">
                          {Array.from({ length: touchCount }, (_, i) => i + 1).map(n => (
                            <button key={n} onClick={() => openTouch(c.id, n)}
                              className={`px-3 py-1 rounded-full text-[11px] font-medium border ${activeTouch === n ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200'}`}>Touch {n}</button>
                          ))}
                        </div>

                        {isSent ? (
                          <div className="border border-green-200 bg-green-50/40 rounded-lg p-3">
                            <p className="text-[11px] text-green-700 mb-1">Sent {d?.sent_at ? ukStamp(d.sent_at) : ''} — this is exactly what {c.first_name || c.email} received</p>
                            <p className="text-sm font-semibold text-gray-900 mb-2 pb-2 border-b border-green-100">{fill(d?.subject ?? '', c, presets)}</p>
                            <div className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderPreview(d?.body ?? '', c, presets, config?.signature ?? '') }} />
                          </div>
                        ) : (
                          <div className="grid lg:grid-cols-2 gap-3">
                            {/* LEFT — editor */}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-medium text-gray-500">Touch {activeTouch} email</span>
                                <div className="flex items-center gap-1.5">
                                  <button onClick={() => saveDraft.mutate({ contactId: c.id, touch: activeTouch, patch: { subject: content.subject, body: content.body, status: d?.status === 'scheduled' ? 'scheduled' : 'draft' } })}
                                    className="text-xs font-medium text-white bg-gray-700 hover:bg-gray-800 px-3 py-1 rounded">{saveDraft.isPending ? 'Saving…' : 'Save'}</button>
                                  <button onClick={() => { if (confirm(`Make this the default Touch ${activeTouch} email for NEW contacts?\n\nExisting contacts are unchanged. New ones start from this template and stay fully editable (e.g. the personalised line).`)) saveConfig.mutate({ templates: { ...(config?.templates ?? {}), [String(activeTouch)]: { subject: content.subject, body: content.body } } }) }}
                                    className="text-xs font-medium text-teal-700 border border-teal-300 bg-teal-50 hover:bg-teal-100 px-2.5 py-1 rounded" title="Save this as the starting template for every new contact's Touch {activeTouch}">Save as default</button>
                                </div>
                              </div>
                              <input value={content.subject} disabled={locked} onChange={e => setContent(c.id, activeTouch, { subject: e.target.value })}
                                placeholder="Subject" className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm font-medium disabled:bg-gray-100" />
                              {!locked && (
                                <div className="flex flex-wrap items-center gap-1 text-[11px]">
                                  <span className="text-gray-400">Click to copy, then paste:</span>
                                  {['{{firstName}}', '{{lastName}}', '{{company}}'].map(tk => (
                                    <button key={tk} onClick={() => copyTag(tk)} title="Copy — then paste into the email" className="px-1.5 py-0.5 rounded border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 font-mono">{copied === tk ? '✓ copied' : tk}</button>
                                  ))}
                                  {presets.map(p => (
                                    <button key={p.tag} onClick={() => copyTag(`{{${p.tag}}}`)} title={`Copy {{${p.tag}}} — ${p.title}`} className="px-1.5 py-0.5 rounded border border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100 font-mono">{copied === `{{${p.tag}}}` ? '✓ copied' : p.title}</button>
                                  ))}
                                </div>
                              )}
                              {locked
                                ? <div className="border border-gray-200 rounded-lg p-3 bg-white text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: content.body || '<span class="text-gray-300">Empty</span>' }} />
                                : <Suspense fallback={<Spinner />}>
                                    <SimpleEmailEditor key={`${c.id}:${activeTouch}`} initialHtml={content.body} onChange={html => setContent(c.id, activeTouch, { body: html })} hidePreview />
                                  </Suspense>}
                              {/* Attachments — shared across all contacts on this touch */}
                              <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                                <span className="text-gray-400">Attachments (Touch {activeTouch}, sent to every contact):</span>
                                {touchAttachments(activeTouch).map(a => (
                                  <span key={a.path} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-gray-200 bg-white text-gray-600">
                                    📎 {a.name} <button onClick={() => removeAttachment(activeTouch, a.path)} className="text-gray-400 hover:text-red-600">×</button>
                                  </span>
                                ))}
                                <button onClick={() => { attTargetRef.current = activeTouch; attFileRef.current?.click() }} disabled={uploadAttachment.isPending}
                                  className="px-1.5 py-0.5 rounded border border-dashed border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50">{uploadAttachment.isPending ? 'Uploading…' : '+ Add file'}</button>
                              </div>
                            </div>

                            {/* RIGHT — live preview of the real email */}
                            <div className="border border-gray-200 rounded-lg bg-white p-3 lg:sticky lg:top-2 self-start">
                              <p className="text-[10px] font-medium text-gray-400 mb-1.5 uppercase tracking-wide">Live preview — sends to {c.first_name || c.email}</p>
                              <p className="text-sm font-semibold text-gray-900 mb-2 pb-2 border-b border-gray-100">{content.subject || <span className="text-gray-300">No subject</span>}</p>
                              <div className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderPreview(content.body, c, presets, config?.signature ?? '') }} />
                              {touchAttachments(activeTouch).length > 0 && <p className="text-[11px] text-gray-500 mt-2 pt-2 border-t border-gray-100">📎 {touchAttachments(activeTouch).map(a => a.name).join(', ')}</p>}
                            </div>

                            {/* Controls — full width under both */}
                            <div className="lg:col-span-2 flex flex-wrap items-center gap-2">
                              <label className="text-[11px] text-gray-500 flex items-center gap-1">
                                {locked ? <Lock className="w-3.5 h-3.5 text-gray-500" /> : <Unlock className="w-3.5 h-3.5 text-gray-400" />}
                                <input type="checkbox" checked={locked} onChange={e => { void commitAndThen(c.id, activeTouch).then(() => saveDraft.mutate({ contactId: c.id, touch: activeTouch, patch: { locked: e.target.checked } })) }} /> Lock
                              </label>
                              <span className="w-px h-4 bg-gray-200" />
                              {activeTouch === 1 ? (
                                <>
                                  <input type="datetime-local" value={schedAt} onChange={e => setSchedAt(e.target.value)} className="rounded-md border border-gray-300 px-2 py-1 text-xs" />
                                  <button onClick={() => commitAndThen(c.id, activeTouch, 'schedule')} className="btn-secondary text-xs flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Schedule</button>
                                </>
                              ) : (
                                <span className="text-[11px] text-gray-500">Auto-sends <strong>+{offsetFor(activeTouch).value} {offsetFor(activeTouch).unit}</strong> after Touch {activeTouch - 1} — set the gap in Flow settings up top.</span>
                              )}
                              <button onClick={() => commitAndThen(c.id, activeTouch, 'send')} disabled={sendNow.isPending} className="text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 px-2.5 py-1 rounded flex items-center gap-1 disabled:opacity-50"><Send className="w-3.5 h-3.5" /> Send now</button>
                              {d?.status === 'scheduled' && (
                                <span className="text-[11px] text-amber-700 flex items-center gap-1">
                                  ⏱ {d.scheduled_for ? ukStamp(d.scheduled_for) : ''}
                                  <button onClick={() => saveDraft.mutate({ contactId: c.id, touch: activeTouch, patch: { status: 'draft', scheduled_for: null } })} className="underline">cancel</button>
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </div>
              ))}
            </div>
          )}
      </section>
    </div>
  )
}
